import React from 'react';
import { RefreshCw, Home } from 'lucide-react';

/**
 * 403 Vector Illustration matching Image 2
 */
const Illustration403 = () => (
  <div className="w-full max-w-lg mx-auto mb-4 select-none">
    <svg viewBox="0 0 800 420" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-auto">
      {/* Ground Line */}
      <line x1="150" y1="360" x2="650" y2="360" stroke="#CBD5E1" strokeWidth="4" strokeLinecap="round" />
      
      {/* Flight Path & Paper Airplane */}
      <path d="M 170 240 C 210 180, 270 330, 290 200" stroke="#B0C4DE" strokeWidth="2.5" strokeDasharray="5 5" fill="none" />
      <g transform="translate(165, 195) rotate(-20)">
        <polygon points="0,15 28,0 20,24 12,18" fill="#38BDF8" />
        <polygon points="28,0 20,24 14,14" fill="#0284C7" />
      </g>

      {/* Number 4 */}
      <text x="170" y="345" fontFamily="system-ui, -apple-system, sans-serif" fontWeight="900" fontSize="240" fill="#E8EEF5">4</text>
      
      {/* Number 0 / Door Arch */}
      <g transform="translate(345, 120)">
        {/* Outer Arch Frame */}
        <path d="M 15 235 L 15 105 A 85 85 0 0 1 185 105 L 185 235 Z" fill="#E8EEF5" stroke="#94A3B8" strokeWidth="3" />
        {/* Inner Door Surface */}
        <path d="M 38 235 L 38 120 A 62 62 0 0 1 162 120 L 162 235 Z" fill="#FFFFFF" stroke="#64748B" strokeWidth="3" />
        {/* Door Circular Porthole */}
        <circle cx="100" cy="95" r="28" fill="#FFFFFF" stroke="#64748B" strokeWidth="3.5" />
        <circle cx="100" cy="95" r="18" fill="#F1F5F9" stroke="#94A3B8" strokeWidth="2.5" />
        {/* Handle */}
        <circle cx="56" cy="165" r="4.5" fill="#475569" />
        <path d="M 52 178 stroke 65 178" stroke="#475569" strokeWidth="3.5" strokeLinecap="round" />
        {/* CLOSE Tag */}
        <g transform="translate(108, 155)">
          <rect x="0" y="0" width="44" height="22" rx="4" fill="#334155" />
          <text x="22" y="15" textAnchor="middle" fill="#FFFFFF" fontSize="9.5" fontWeight="bold" fontFamily="sans-serif">CLOSE</text>
          <line x1="22" y1="0" x2="22" y2="-15" stroke="#64748B" strokeWidth="2" />
        </g>
      </g>

      {/* Number 3 */}
      <text x="500" y="345" fontFamily="system-ui, -apple-system, sans-serif" fontWeight="900" fontSize="240" fill="#E8EEF5">3</text>

      {/* Leaf Plant */}
      <g transform="translate(615, 275)">
        <path d="M 12 75 L 24 25 L 44 25 L 56 75 Z" fill="#94A3B8" />
        <path d="M 22 25 C -5 -10, -25 15, 15 35" fill="#38BDF8" opacity="0.75" />
        <path d="M 34 25 C 55 -15, 75 10, 42 35" fill="#0284C7" opacity="0.8" />
      </g>

      {/* Left Character (Sitting in Teal/Blue) */}
      <g transform="translate(305, 305)">
        <circle cx="45" cy="12" r="11" fill="#FB923C" />
        <path d="M 38 8 C 38 -2, 52 -2, 50 8 Z" fill="#1E293B" />
        <path d="M 28 25 C 28 20, 54 20, 54 45 L 18 45 Z" fill="#0D9488" />
        <path d="M 18 45 L -12 55 L 14 55 Z" fill="#0F766E" />
        <rect x="20" y="28" width="14" height="18" rx="2" fill="#E2E8F0" transform="rotate(-15 20 28)" />
      </g>

      {/* Right Character (Standing with Dark Shirt & Red Pants) */}
      <g transform="translate(460, 240)">
        <circle cx="30" cy="18" r="12" fill="#FB923C" />
        <path d="M 20 14 C 20 2, 38 2, 38 12 Z" fill="#1E293B" />
        <path d="M 16 32 L 44 32 L 40 75 L 20 75 Z" fill="#334155" />
        <path d="M 18 36 L -10 30 L -8 24 L 20 30 Z" fill="#334155" />
        <circle cx="-10" cy="27" r="4" fill="#FB923C" />
        <rect x="20" y="75" width="9" height="45" fill="#EF4444" rx="3" />
        <rect x="31" y="75" width="9" height="45" fill="#DC2626" rx="3" />
        <ellipse cx="22" cy="120" rx="8" ry="4" fill="#1E293B" />
        <ellipse cx="37" cy="120" rx="8" ry="4" fill="#1E293B" />
      </g>
    </svg>
  </div>
);

/**
 * PermissionDenied component to display permissions/access denied and loading error states nicely.
 * Matches user's exact designs for Banner (Image 1) and Full 403 Page (Image 2).
 */
const PermissionDenied = ({
  variant = 'card',
  title = 'Access Restricted',
  message = 'Access denied: contact admin for permissions',
  onRetry = null,
  onGoHome = null,
  onGoBack = null,
  isRetrying = false,
}) => {
  const handleGoBack = () => {
    if (onGoBack) {
      onGoBack();
    } else if (typeof window !== 'undefined' && window.history.length > 1) {
      window.history.back();
    } else if (onGoHome) {
      onGoHome();
    }
  };

  const handleGoHome = () => {
    if (onGoHome) {
      onGoHome();
    } else if (typeof window !== 'undefined') {
      window.location.href = '/';
    }
  };

  // Image 1 Style: Inline Red Banner Box with Circular ( ! ) Icon & Red Pill Retry Button
  if (variant === 'banner') {
    return (
      <div className="w-full bg-[#FFF2F2] border border-[#FFDADA] rounded-2xl p-4 my-4 shadow-sm flex flex-wrap items-center justify-between gap-4 animate-in fade-in duration-300">
        <div className="flex items-center gap-3.5">
          {/* Circular ( ! ) Icon */}
          <div className="w-9 h-9 rounded-full border-2 border-[#E12533] text-[#E12533] font-black flex items-center justify-center text-sm shrink-0 bg-white shadow-xs">
            !
          </div>
          <div>
            <h4 className="text-xs font-black uppercase tracking-wider text-[#E12533]">
              {title || 'ERROR'}
            </h4>
            <p className="text-sm font-semibold text-[#991B1B]">
              {message}
            </p>
          </div>
        </div>

        {onRetry && (
          <button
            onClick={onRetry}
            disabled={isRetrying}
            className="px-5 py-2 bg-[#E12533] hover:bg-[#C91C29] active:scale-95 text-white font-bold text-xs rounded-full shadow-md shadow-red-500/20 transition-all disabled:opacity-50 flex items-center gap-1.5 shrink-0"
          >
            <RefreshCw size={13} className={isRetrying ? 'animate-spin' : ''} />
            <span>Retry</span>
          </button>
        )}
      </div>
    );
  }

  // Image 2 Style: Full Page 403 Vector Illustration
  if (variant === 'fullPage') {
    return (
      <div className="min-h-[85vh] flex flex-col items-center justify-center p-6 text-center bg-white dark:bg-slate-950 animate-in fade-in duration-500">
        {/* 403 Illustration Graphic */}
        <Illustration403 />

        {/* Text Content */}
        <div className="max-w-md space-y-3">
          <h1 className="text-3xl sm:text-4xl font-extrabold text-[#2D2D52] dark:text-white tracking-tight">
            We are Sorry...
          </h1>
          <p className="text-sm sm:text-base font-medium text-[#5D5D82] dark:text-slate-400 leading-relaxed">
            The page you're trying to access has restricted access.
            <br />
            Please refer to your system administrator
          </p>
        </div>

        {/* Action Button */}
        <div className="mt-8">
          <button
            onClick={handleGoBack}
            className="px-10 py-3.5 bg-[#00BDD6] hover:bg-[#00A5BC] active:scale-95 text-white font-bold text-sm rounded-full shadow-lg shadow-[#00BDD6]/40 hover:shadow-[#00BDD6]/60 hover:scale-105 transition-all"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  // Compact Centered Card
  if (variant === 'compact') {
    return (
      <div className="min-h-[50vh] flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 rounded-[2.5rem] shadow-xl p-8 text-center space-y-6 animate-in fade-in zoom-in duration-300">
          <div className="w-12 h-12 rounded-full border-2 border-[#E12533] text-[#E12533] font-black flex items-center justify-center text-lg mx-auto bg-rose-50">
            !
          </div>

          <p className="text-base font-bold text-[#E12533] dark:text-rose-400 leading-snug px-2">
            {message.startsWith('Error:') ? message : `Error: ${message}`}
          </p>

          {onRetry && (
            <div className="pt-2">
              <button
                onClick={onRetry}
                disabled={isRetrying}
                className="px-8 py-3 bg-[#E12533] hover:bg-[#C91C29] active:scale-95 text-white font-bold text-sm rounded-full shadow-lg shadow-red-500/25 transition-all disabled:opacity-50 inline-flex items-center gap-2"
              >
                <RefreshCw size={16} className={isRetrying ? 'animate-spin' : ''} />
                <span>Retry</span>
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Standard Card Box
  return (
    <div className="min-h-[70vh] flex items-center justify-center p-4">
      <div className="max-w-lg w-full bg-white dark:bg-slate-900 border border-slate-200/80 dark:border-slate-800 rounded-3xl shadow-xl shadow-slate-200/50 dark:shadow-none p-8 text-center space-y-6 animate-in fade-in zoom-in duration-300">
        <div className="flex items-center justify-center gap-3 text-[#E12533]">
          <div className="w-10 h-10 rounded-full border-2 border-[#E12533] text-[#E12533] font-black flex items-center justify-center text-base shrink-0 bg-rose-50">
            !
          </div>
          <h2 className="text-xl font-bold text-slate-900 dark:text-white tracking-tight">
            {title}
          </h2>
        </div>

        <div className="bg-[#FFF2F2] border border-[#FFDADA] rounded-2xl p-4 text-[#991B1B] text-sm font-semibold">
          {message}
        </div>

        <div className="flex flex-wrap justify-center gap-3 pt-2">
          {onRetry && (
            <button
              onClick={onRetry}
              disabled={isRetrying}
              className="px-6 py-2.5 bg-[#E12533] hover:bg-[#C91C29] active:scale-95 text-white font-bold text-sm rounded-xl shadow-md shadow-red-500/20 transition-all disabled:opacity-50 flex items-center gap-2"
            >
              <RefreshCw size={16} className={isRetrying ? 'animate-spin' : ''} />
              <span>Retry</span>
            </button>
          )}

          <button
            onClick={handleGoHome}
            className="px-6 py-2.5 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 active:scale-95 text-slate-700 dark:text-slate-200 font-bold text-sm rounded-xl transition-all flex items-center gap-2"
          >
            <Home size={16} />
            <span>Go Home</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default PermissionDenied;
